import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";

export interface MainLayout {
  id: string;
  name: string;
  folder_structure?: string | NavigationItem[]; // Update type to allow both string (from API) and parsed array
}

export interface NavigationItem {
  icon?: string;
  id: string;
  entity_name?: string;
  entity_id?: string;
  show_in_nav?: boolean;
  type?: "folder" | "entity";
  navigation_items?: {
    data: NavigationItem[];
  };
  name?: string;
  parentId?: string | null;
}

const mainLayoutQueryDocument = gql`
  query MainLayout {
    mainLayoutList(
      filter: { query: { condition: { field: "name", operator: equal, value: "main-layout" } } }
    ) {
      data {
        id
        name
        folder_structure
      }
    }
  }
`;

export const getMainLayout = async () => {
  try {
    const client = createGraphQLClient({ endpointName: "mainLayoutList" });

    const mainLayout = (await client.request(mainLayoutQueryDocument, {})) as {
      mainLayoutList: {
        data: MainLayout[];
      };
    };

    if (!mainLayout.mainLayoutList.data[0]) {
      throw Error("error fetching main layout");
    }

    if (mainLayout.mainLayoutList.data[0].folder_structure) {
      // Use type assertion to help TypeScript understand the parsing
      const parsedStructure = JSON.parse(
        mainLayout.mainLayoutList.data[0].folder_structure as string,
      );

      // Validate and assign the parsed structure
      if (Array.isArray(parsedStructure)) {
        mainLayout.mainLayoutList.data[0].folder_structure = parsedStructure as NavigationItem[];
      }
    }

    return {
      ...mainLayout.mainLayoutList.data[0],
      fetchedAt: new Date(),
    };
  } catch (error) {
    console.error("error fetching main layout", error);
    throw error;
  }
};

const updateMainLayoutFolderStructureMutationDocument = gql`
  mutation UpdateMainLayoutFolderStructure($id: ID!, $folderStructure: String!) {
    updateMainLayout(input: { id: $id, folder_structure: $folderStructure }) {
      data {
        id
      }
    }
  }
`;

export const updateMainLayoutFolderStructure = async ({
  id,
  folderStructure,
}: {
  id: string;
  folderStructure: Array<NavigationItem>;
}) => {
  const client = createGraphQLClient({ endpointName: "updateMainLayout" });

  try {
    const result = (await client.request(updateMainLayoutFolderStructureMutationDocument, {
      id,
      folderStructure: JSON.stringify(folderStructure),
    })) as {
      updateMainLayout: {
        data: MainLayout;
      };
    };

    return result.updateMainLayout.data;
  } catch (error) {
    console.error("error updating main layout folder structure", error);
    throw error; // Re-throw the error to handle it in the calling code
  }
};
